import { CheckOutlined, ClearOutlined, FileOutlined, SettingOutlined } from '@ant-design/icons';
import { Button, notification } from 'antd';
import React, { ReactElement } from 'react';
import { batch } from 'react-redux';
import { alarmApi } from '../../../../api';
import { getPermissionErrorMessage, SubPermissions, User } from '../../../../model/AccountModel';
import {
	AcknowledgedAndClearAlarmsPayload,
	AlarmAcknowledgementActions,
	AlarmActionOptions,
	AlarmModel,
	AlarmNoteForAcknowledge,
	AlarmState
} from '../../../../model/AlarmModel';
import { AlarmQueueTreeNode, PendingItem } from '../../../../model/GraphicModel';
import { useStoreDispatch, useStoreSelector } from '../../../../store';
import { setAlarmNoteForAcknowledge, setShowAlarmAddOperatorNote } from '../../../../store/alarm/actions';
import { graphicAlarmPermissionsSelector, mapInfoSelector } from '../../../../store/graphic/selectors';
import { getAlarmNoteAddOperatorNoteModal, getMultipleAlarmAction, isNoteRequired, selectAllAlarms } from '../../../alarms/helpers';
import { NotificationStatus } from '../../../common';
import styles from '../alarmQueue.module.scss';
import { acknowledgeAllIcon, clearAllIcon, getActualSelectedAlarmKeys, getAllAlarmIds, getAllAlarmModels, getAllPendingIds, mergeArrays } from '../helper';

const user: User = getUser();
const isAcknowledgedAlarmAllowed: boolean = User.getSubComponentPermission(user, SubPermissions.AlarmViewer_AlarmsRTNs_Acknowledge).allowed;
const isClearAlarmAllowed: boolean = User.getSubComponentPermission(user, SubPermissions.AlarmViewer_AlarmsRTNs_ClearinStandardMode).allowed;
const isAddNoteAllowed: boolean = User.getSubComponentPermission(user, SubPermissions.AlarmViewer_AlarmViewer_AddNotes).allowed;

type Props = {
	checkedKeys: string[];
	treeData: AlarmQueueTreeNode[];
	pendingIds: PendingItem[];
	setIsLoading: (value: boolean) => void;
};

type AcknowledgedAndClearAlarmsQueuePayload = Omit<AcknowledgedAndClearAlarmsPayload, 'addOperatorNoteCallback' | 'acknowledgeAndClearCallback'> & {
	addOperatorNoteCallback: (
		pendingIds: PendingItem[],
		action: AlarmAcknowledgementActions,
		payload: AcknowledgedAndClearAlarmsQueuePayload,
		isAcknowledge: boolean
	) => Promise<AlarmNoteForAcknowledge>;
	acknowledgeAndClearCallback: (payload: AcknowledgedAndClearAlarmsQueuePayload) => Promise<AlarmNoteForAcknowledge>;
};

const AlarmActionButtons = ({ checkedKeys, treeData, pendingIds, setIsLoading }: Props) => {
	const dispatch = useStoreDispatch();

	const { Id } = useStoreSelector(mapInfoSelector);
	const { IsClearUnacknowledgedAllowed, CanAcknowledgeMultiple, CanClearMultiple } = useStoreSelector(graphicAlarmPermissionsSelector);

	const handleActionDropdownAsync = async (option: AlarmAcknowledgementActions | AlarmActionOptions) => {
		let payload: AcknowledgedAndClearAlarmsQueuePayload = {
			action: option as AlarmAcknowledgementActions,
			operatorNotes: [],
			alarmIds: [],
			acknowledgeAndClearCallback: acknowledgeAndClearAlarmCommon,
			addOperatorNoteCallback,
			setAlarmNoteForAcknowledgeCallback,
			setShowAlarmAddOperatorNoteCallback,
		};

		const isAcknowledge = option === AlarmAcknowledgementActions.AcknowledgedMultiple || option === AlarmAcknowledgementActions.AcknowledgedOne;

		switch (option) {
			case AlarmAcknowledgementActions.AcknowledgedOne:
			case AlarmAcknowledgementActions.ClearOneAcknowledged:
				const actualSelectedAlarmIds: number[] = await getActualSelectedAlarmKeys(
					pendingIds,
					checkedKeys,
					IsClearUnacknowledgedAllowed,
					isAcknowledge,
					false
				);

				if (actualSelectedAlarmIds.length > 0) {
					payload.alarmIds = actualSelectedAlarmIds;
					payload.action = getMultipleAlarmAction(actualSelectedAlarmIds, option);

					if (
						actualSelectedAlarmIds.length === 1 ||
						(CanAcknowledgeMultiple && payload.action === AlarmAcknowledgementActions.AcknowledgedMultiple) ||
						(CanClearMultiple && payload.action === AlarmAcknowledgementActions.ClearMultipleAcknowledged)
					) {
						await acknowledgedAndClearAlarms(pendingIds, payload, isAcknowledge);
					} else {
						notification['error']({
							message: _('MissingAlarmPermission'),
						});
					}
				}
				break;

			case AlarmAcknowledgementActions.AcknowledgedMultiple:
			case AlarmAcknowledgementActions.ClearMultipleUnacknowledged:
				const alarmsModel: AlarmModel[] = await getAllAlarmIds([Id], isAcknowledge);

				if (alarmsModel.length > 0) {
					const allPendingIds: PendingItem[] = mergeArrays<PendingItem>(pendingIds, getAllPendingIds(treeData), (a, b) => a.Address === b.Address);
					payload.alarmIds = selectAllAlarms(alarmsModel).map(x => Number(x));
					payload.alarmsDetails = alarmsModel;
					await acknowledgedAndClearAlarms(allPendingIds, payload, isAcknowledge);
				}
				break;

			case AlarmActionOptions.ADD_OPERATOR_NOTE:
				const addNoteAlarmIds: number[] = await getActualSelectedAlarmKeys(pendingIds, checkedKeys, IsClearUnacknowledgedAllowed, isAcknowledge, true);

				await openAddOperatorNoteModal(pendingIds, AlarmAcknowledgementActions.None, false, addNoteAlarmIds);
				break;
		}
	};

	const openAddOperatorNoteModal = async (
		pendingIds: PendingItem[],
		action: AlarmAcknowledgementActions,
		isAlarmNoteForAcknowledge: boolean,
		alarmIds: number[]
	) => {
		const alarmNote = getAlarmNoteAddOperatorNoteModal(
			action,
			isAlarmNoteForAcknowledge,
			alarmIds,
			await getDataSource(action, pendingIds, isAlarmNoteForAcknowledge)
		);
		batch(() => {
			dispatch(setShowAlarmAddOperatorNote(true));
			dispatch(setAlarmNoteForAcknowledge(alarmNote));
		});
	};

	const getDataSource = async (action: AlarmAcknowledgementActions, pendingIds: PendingItem[], isAcknowledge: boolean) => {
		let pendingAlarms: AlarmModel[] = [];
		let stateAlarms: AlarmModel[] = [];

		if (pendingIds.length > 0) {
			pendingAlarms = await getAllAlarmModels(pendingIds, isAcknowledge);
		}

		treeData
			.filter(x => x.children !== undefined)
			.forEach(y =>
				stateAlarms.push(
					...y.children.map<AlarmModel>((x: AlarmQueueTreeNode) => {
						const titleElement: ReactElement = x.title as ReactElement;
						return titleElement.props.data;
					})
				)
			);

		let allAlarms: AlarmModel[] = mergeArrays<AlarmModel>(
			pendingAlarms,
			stateAlarms,
			(a, b) => a.AlarmDetails[0].ActiveAlarmId === b.AlarmDetails[0].ActiveAlarmId
		);

		if (
			!IsClearUnacknowledgedAllowed &&
			(action === AlarmAcknowledgementActions.ClearMultipleUnacknowledged ||
				action === AlarmAcknowledgementActions.ClearOneAcknowledged ||
				action === AlarmAcknowledgementActions.ClearMultipleAcknowledged)
		) {
			allAlarms = allAlarms.filter(x => x.State === AlarmState.Acknowledged);
		}

		return allAlarms;
	};

	const addOperatorNoteCallback = async (
		pendingIds: PendingItem[],
		action: AlarmAcknowledgementActions,
		payload: AcknowledgedAndClearAlarmsQueuePayload,
		isAcknowledge: boolean
	) => {
		return getAlarmNoteAddOperatorNoteModal(action, true, payload.alarmIds, await getDataSource(action, pendingIds, isAcknowledge), payload.alarmsDetails);
	};

	const setAlarmNoteForAcknowledgeCallback = (alarmNote: AlarmNoteForAcknowledge) => {
		dispatch(setAlarmNoteForAcknowledge(alarmNote));
	};

	const setShowAlarmAddOperatorNoteCallback = () => {
		dispatch(setShowAlarmAddOperatorNote(true));
	};

	const acknowledgedAndClearAlarms = async (pendingIds: PendingItem[], payload: AcknowledgedAndClearAlarmsQueuePayload, isAcknowledge: boolean) => {
		const {
			action,
			acknowledgeAndClearCallback,
			addOperatorNoteCallback,
			setAlarmNoteForAcknowledgeCallback,
			setShowAlarmAddOperatorNoteCallback,
			saveChangesCallback,
		} = payload;

		const noteRequired = isNoteRequired(action);

		let alarmNote: AlarmNoteForAcknowledge;
		if (noteRequired) {
			alarmNote = await addOperatorNoteCallback(pendingIds, action, payload, isAcknowledge);

			setShowAlarmAddOperatorNoteCallback();
			setAlarmNoteForAcknowledgeCallback(alarmNote);
		} else {
			alarmNote = await acknowledgeAndClearCallback(payload);

			if (alarmNote) {
				setAlarmNoteForAcknowledgeCallback(alarmNote);
			}

			saveChangesCallback?.();
		}
	};

	const acknowledgeAndClearAlarmCommon = ({ action, alarmIds, operatorNotes }: AcknowledgedAndClearAlarmsQueuePayload): Promise<AlarmNoteForAcknowledge> =>
		new Promise(resolve => {
			let alarmNote: AlarmNoteForAcknowledge = undefined;

			alarmApi
				.acknowledgeAndClearAlarms(action, alarmIds, operatorNotes)
				.then(res => {
					NotificationStatus({
						responseData: res,
						notUseDefaultNotification: true,
						onFailedValidation: () => {
							notification['info']({
								message: _('AdditionalPermissionNotReturnedToNormalAlarms'),
							});
						},
					});
				})
				.finally(() => {
					if (isNoteRequired(action)) {
						alarmNote = {
							isAlarmNoteForAcknowledge: false,
							acknowledgeAction: AlarmAcknowledgementActions.None,
							alarms: [],
						} as AlarmNoteForAcknowledge;
					}
					resolve(alarmNote);
				});
		});

	const handleActionDropdown = (option: AlarmAcknowledgementActions | AlarmActionOptions) => {
		setIsLoading(true);
		handleActionDropdownAsync(option).finally(() => setIsLoading(false));
	};

	const isAlarmSelected = checkedKeys.length > 0;
	const isAlarmTableEmpty = treeData.length === 0;
	const isAckSelectedOptionDisabled = (!isAlarmSelected && isAcknowledgedAlarmAllowed) || !isAcknowledgedAlarmAllowed;
	const isClearAlarmOptionDisabled = (!isAlarmSelected && isClearAlarmAllowed) || !isClearAlarmAllowed;
	const isAddOperatorNoteDisabled = (!isAlarmSelected && isAddNoteAllowed) || !isAddNoteAllowed;

	return (
		<div className={styles.actionMenuContainer}>
			<div className={styles.actionMenu}>
				<Button
					id='acknowledgeAlarmButton'
					type='text'
					icon={<CheckOutlined className={styles.acknowledgeIcon} />}
					disabled={isAckSelectedOptionDisabled}
					className={styles.actionButton}
					title={getPermissionErrorMessage(isAcknowledgedAlarmAllowed) || _('AcknowledgedSelected')}
					onClick={() => handleActionDropdown(AlarmAcknowledgementActions.AcknowledgedOne)}
				/>
				<Button
					id='acknowledgeAllAlarmsButton'
					type='text'
					icon={acknowledgeAllIcon}
					disabled={!CanAcknowledgeMultiple || isAlarmTableEmpty}
					className={styles.actionButton}
					title={getPermissionErrorMessage(CanAcknowledgeMultiple) || _('AcknowledgedAll')}
					onClick={() => handleActionDropdown(AlarmAcknowledgementActions.AcknowledgedMultiple)}
				/>
				<Button
					id='clearAlarmsButton'
					type='text'
					icon={<ClearOutlined className={styles.clearIcon} />}
					disabled={isClearAlarmOptionDisabled}
					className={styles.actionButton}
					title={getPermissionErrorMessage(isClearAlarmAllowed) || _('ClearSelected')}
					onClick={() => handleActionDropdown(AlarmAcknowledgementActions.ClearOneAcknowledged)}
				/>
				<Button
					id='clearAllAlarmsButton'
					type='text'
					icon={clearAllIcon}
					disabled={!CanClearMultiple || isAlarmTableEmpty}
					className={styles.actionButton}
					title={getPermissionErrorMessage(CanClearMultiple) || _('ClearAll')}
					onClick={() => handleActionDropdown(AlarmAcknowledgementActions.ClearMultipleUnacknowledged)}
				/>
				<Button
					id='addNoteButton'
					type='text'
					icon={<FileOutlined className={styles.noteIcon} />}
					disabled={isAddOperatorNoteDisabled}
					className={styles.actionButton}
					title={getPermissionErrorMessage(isAddNoteAllowed) || _('AddOperatorNote')}
					onClick={() => handleActionDropdown(AlarmActionOptions.ADD_OPERATOR_NOTE)}
				/>
			</div>
			<div>
				<Button id='configurationButton' type='text' icon={<SettingOutlined />} className={styles.actionButton} />
			</div>
		</div>
	);
};

export { AlarmActionButtons };

